import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {isEmpty} from 'lodash/lang';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import {Paper} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        marginTop: theme.spacing(3),
        overflowX: 'auto',
    },
    table: {
        minWidth: 650,
    },
}));

const defaultHiddenFields = ['id', 'record'];

const buildTable = (records, settings, extraHiddenFields = [], restProps) => {

    const headerRows = [];
    const bodyRows = [];
    const hiddenFields = new Set([
        ...defaultHiddenFields,
        ...extraHiddenFields,
    ]);

    const {onRowClick, sortingManager, onSortableFieldClick} = restProps;
    delete restProps.onRowClick;
    delete restProps.sortingManager;
    delete restProps.onSortableFieldClick;

    for (let fieldName of Object.keys(settings)) {
        if (hiddenFields.has(fieldName)) {
            continue;
        }
        const field = settings[fieldName];
        headerRows.push({
            name: fieldName,
            text: field.fieldText,
            extra: field.fieldExtra || '',
            sortable: field.sortable || false,
            class: field.class || '',
            noPadding: field.noPadding,
        });
        if (field.sortable && !sortingManager.fields[fieldName]) {
            sortingManager.register(fieldName);
        }
    }

    const tableHeader = (
        <TableHead>
            <TableRow>
                {
                    headerRows.map(field => (
                        <TableCell
                            key={`header-cell-${field.text}`}
                            className={
                                `${classNames({
                                    'header-cell-sortable': field.sortable,
                                })} ${field.class ? field.class : ''} 
                                ${field.noPadding ? 'thinkscape-table-cell-padding' : ''}`}
                            onClick={!field.sortable ? undefined : () => (
                                sortingManager.clickCaret(field.name)
                                    .then(sortingFieldStatus => onSortableFieldClick(field.name, sortingFieldStatus))
                            )}
                        >
                            <span>{field.text} </span>
                            {field.extra}
                            {sortingManager && sortingManager.carets[field.name]}
                        </TableCell>
                    ))
                }
            </TableRow>
        </TableHead>
    );

    if (isEmpty(records)) {
        return {
            tableHeader,
            tableBody: (
                <TableBody>
                    <TableRow>
                        <TableCell colSpan={headerRows.length}>No Data</TableCell>
                    </TableRow>
                </TableBody>
            ),
        };
    }

    for (let record of records) {
        const row = [];
        for (let [fieldName, fieldValue] of Object.entries(record)) {
            if (hiddenFields.has(fieldName)) {
                continue;
            }
            if (settings[fieldName].hasOwnProperty('formatData')) {
                fieldValue = settings[fieldName].formatData(fieldValue, record, restProps);
            }
            row.push({fieldValue: fieldValue, noPadding: settings[fieldName].noPadding, class: settings[fieldName].class});
        }
        bodyRows.push([record.id, row]);
    }

    const tableBody = (
        <TableBody>
            {
                bodyRows.map(([id, fieldValues]) => (
                    <TableRow
                        key={`body-${id}`}
                        className={classNames({
                            'cursor-pointer': !!onRowClick,
                        })}
                        onClick={onRowClick ? () => onRowClick(records.find(record => record.id === id)) : undefined}
                    >
                        {
                            fieldValues.map((value, idx) => (
                                <TableCell
                                    className={`${value.noPadding ? 'thinkscape-table-cell-padding' : ''} 
                                        ${value.class ? value.class : ''}`}
                                    padding={value.noPadding ? 'none' : undefined}
                                    key={`body-cell-${idx}`}>
                                    {value.fieldValue}
                                </TableCell>
                            ))
                        }
                    </TableRow>
                ))
            }
        </TableBody>
    );
    return {tableHeader, tableBody};
};

const ThinkscapeTableCore = ({records, settings, hiddenFields, ...restProps}) => {
    const {tableHeader, tableBody} = buildTable(records, settings, hiddenFields, restProps);
    const classes = useStyles();

    return (
        <Paper className={classes.root}>
            <Table className={classes.table}>
                {tableHeader}
                {tableBody}
            </Table>
        </Paper>
    );
};

ThinkscapeTableCore.propTypes = {
    records: PropTypes.array.isRequired,
    settings: PropTypes.object.isRequired,
    hiddenFields: PropTypes.array,
};

ThinkscapeTableCore.defaultProps = {
    hiddenFields: [],
};

export default ThinkscapeTableCore;
